import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import type { EventPropertiesMap } from 'contexts/analytics/types';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import { useIsFirstModalView } from 'hooks/useIsFirstModalView';
import { setModalOpened } from 'store/modal';

type ConditionsSlideDetails = Pick<
  EventPropertiesMap['First Look'],
  'condition_name' | 'body_area' | 'flags_found' | 'flags_total'
>;

type UseConditionsSlideArgs = {
  slideId: string;
  /** The condition whose detail page the CTA opens; the conditions page if omitted. */
  condition?: string;
  /** Extra properties for the slide's view event. */
  viewed?: ConditionsSlideDetails;
  /** Extra properties for the slide's CTA event. */
  ctaClicked?: ConditionsSlideDetails;
};

/**
 * A First Look conditions slide's events and call to action: reports the view
 * once on mount, and returns the handler that leaves First Look for conditions.
 */
export function useConditionsSlide({
  slideId,
  condition,
  viewed,
  ctaClicked,
}: UseConditionsSlideArgs) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const { currentKit, currentKitId } = useCurrentKitAndTinyAccount();
  const isFirstView = useIsFirstModalView('firstLook');

  useEffect(() => {
    trackEvent('First Look', {
      event_type: 'conditions_slide_viewed',
      slide_id: slideId,
      ...viewed,
      kit_type: currentKit?.sampling_type,
      kit_id: currentKit?.id,
      is_rewatch: !isFirstView,
    });
  }, []);

  const viewConditions = () => {
    trackEvent('First Look', {
      event_type: 'conditions_cta_clicked',
      slide_id: slideId,
      ...ctaClicked,
      kit_type: currentKit?.sampling_type,
      kit_id: currentKit?.id,
    });
    dispatch(setModalOpened({ name: 'firstLook', props: {} }));
    if (currentKitId) {
      const conditionsPath = `/results/${currentKitId}/conditions/metrics`;
      navigate(condition ? `${conditionsPath}/${condition}` : conditionsPath);
    }
  };

  return { isFirstView, viewConditions };
}

export default useConditionsSlide;
